// Merge a D&D Beyond sheet read (connector DM_SCREEN_SCAN_RESULT sheet object)
// into a Character record. Sheet-owned fields are replaced; DM-owned fields
// (colour, conditions, notes, effects, exhaustion, death saves, followers) are
// kept unless the sheet supplies them.
import { initialsOf, type Character } from "./types";
import { applyOverrides } from "./overrides";

export interface SheetRead {
  sourceId: string; sourceUrl?: string; name: string; detail?: string; campaign?: string;
  hp?: number; maxHp?: number; tempHp?: number; ac?: number; initiative?: number; proficiencyBonus?: number;
  abilities?: Character["abilities"]; savingThrows?: Character["savingThrows"];
  passivePerception?: number; passiveInsight?: number; passiveInvestigation?: number;
  spellSaveDc?: number | null; spellcasting?: unknown[]; preparedSpells?: unknown[]; timedClassFeatures?: unknown[];
  coins?: Character["coins"]; coinLocations?: unknown[]; inventoryContainers?: unknown[]; inventoryItems?: unknown[];
  trainedSkills?: unknown[]; languages?: string[]; toolProficiencies?: string[]; equippedItems?: unknown[]; magicItems?: unknown[];
  movement?: Character["movement"]; vision?: Character["vision"]; conditions?: Character["conditions"]; dmNoteRecords?: unknown[];
  [k: string]: unknown;
}

const SHEET_FIELDS: (keyof Character)[] = [
  "name", "detail", "hp", "maxHp", "tempHp", "ac", "initiative", "proficiencyBonus", "abilities", "savingThrows",
  "passivePerception", "spellSaveDc", "spellcasting", "preparedSpells", "timedClassFeatures", "coins", "coinLocations",
  "inventoryContainers", "inventoryItems", "trainedSkills", "languages", "toolProficiencies", "equippedItems", "magicItems",
  "movement", "vision", "dmNoteRecords",
];
const COLORS = ["violet", "amber", "teal", "rose", "emerald", "sky", "gold", "slate"];

export const characterIdForSheet = (sheet: Pick<SheetRead, "sourceId">) => `ddb-${sheet.sourceId}`;

export function mergeSheet(existing: Character | undefined, sheet: SheetRead, index = 0): Character {
  const base: Character = existing ?? {
    id: characterIdForSheet(sheet), initials: initialsOf(sheet.name), name: sheet.name, detail: sheet.detail || "",
    hp: 0, maxHp: 0, tempHp: 0, ac: 10, initiative: 0, passivePerception: 10, insight: 10, investigation: 10,
    abilities: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 }, savingThrows: { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 },
    proficiencyBonus: 2, spellSaveDc: null, spellcasting: [], preparedSpells: [], timedClassFeatures: [],
    coins: { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 }, coinLocations: [], inventoryContainers: [], inventoryItems: [], trainedSkills: [],
    languages: [], toolProficiencies: [], equippedItems: [], magicItems: [], followers: [], dmNoteRecords: [],
    initiativeAdvantage: false, cannotBeSurprised: false, exhaustion: 0, movement: [], vision: [], conditions: [],
    color: COLORS[index % COLORS.length], deathSuccesses: 0, deathFailures: 0, revivalPenalty: 0, effectLevels: {},
  };
  const next: Character = { ...base, id: characterIdForSheet(sheet) };
  for (const key of SHEET_FIELDS) if (sheet[key as string] !== undefined) (next as any)[key] = sheet[key as string];
  if (sheet.passiveInsight !== undefined) next.insight = Number(sheet.passiveInsight);
  if (sheet.passiveInvestigation !== undefined) next.investigation = Number(sheet.passiveInvestigation);
  if (Array.isArray(sheet.conditions) && sheet.conditions.length) next.conditions = sheet.conditions;
  next.initials = initialsOf(next.name);
  // The sheet's max already includes its Max HP Modifier: derive the base so
  // later modifier changes (drains, Aid) move the max by exactly their delta.
  if (Number.isFinite(Number(next.maxHp)) && next.maxHpModifier !== undefined) next.maxHpBase = Number(next.maxHp) - Number(next.maxHpModifier || 0);
  // LAST, so a sync cannot overwrite what the DM set by hand. Every field above
  // came from the sheet; these are the DM's answers and they win.
  return applyOverrides(next);
}
